import {
  createHero,
  generateEquipment,
  addXp,
  HERO_CHARACTER_NAMES,
  TOME_DEFINITIONS,
} from '@roe/shared';
import type { GameStateManager } from '../state/GameStateManager.js';
import type { HeroClass, ItemRarity } from '@roe/shared';
import { AuthService } from '../services/AuthService.js';
import { SyncService } from '../services/SyncService.js';
import { AdminPanel } from './AdminPanel.js';

function getApiBase(): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') return 'http://localhost:3000';
  return '';
}

type PhaserGame = { scene: { getScene: (key: string) => unknown } };

const VALID_CLASSES: HeroClass[] = ['warrior', 'wizard', 'archer', 'healer', 'assassin', 'tank', 'druid'];
const VALID_RARITIES: ItemRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

const RARITY_TO_TIER: Record<string, number> = {
  common: 0,
  uncommon: 5,
  rare: 15,
  epic: 50,
  legendary: 200,
};

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

type CommandDef = {
  description: string;
  execute: (args: string[], gsm: GameStateManager, game?: PhaserGame) => string | Promise<string>;
};

const COMMANDS: Record<string, CommandDef> = {
  gold: {
    description: '/gold set <amount> — Set gold | /gold add <amount> — Add gold | /gold set <username> <amount> — Set gold (admin) | /gold add <username> <amount> — Add gold (admin)',
    execute: async (args, gsm) => {
      // Must have at least: /gold set|add <amount>
      const mode = args[0] as string | undefined;
      if (mode !== 'set' && mode !== 'add') {
        return 'Usage: /gold set <amount> | /gold add <amount> | /gold set <username> <amount> | /gold add <username> <amount>';
      }
      // Admin grant: /gold set|add <username> <amount>
      if (args.length >= 3) {
        const auth = AuthService.getInstance();
        if (!auth.isAdmin()) return 'Admin access required';
        const username = args[1];
        const amount = parseInt(args[2], 10);
        if (isNaN(amount)) return 'Usage: /gold set <username> <amount>';
        try {
          const res = await fetch(`${getApiBase()}/api/admin/grant-gold`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.getToken()}`,
            },
            body: JSON.stringify({ username, amount, mode }),
          });
          const data = await res.json() as { error?: string; total?: number };
          if (!res.ok) return `Error: ${data.error ?? 'request failed'}`;
          return mode === 'set'
            ? `Gold set to ${data.total} for "${username}"`
            : `Added ${amount} gold to "${username}" (total: ${data.total})`;
        } catch (e) {
          return `Request failed: ${e instanceof Error ? e.message : 'unknown error'}`;
        }
      }
      // Self: /gold set|add <amount>
      const n = parseInt(args[1], 10);
      if (isNaN(n)) return 'Usage: /gold set <amount> | /gold add <amount>';
      const current = mode === 'add' ? gsm.player.resources.gold : 0;
      gsm.player.resources.gold = clamp(current + n, 0, 999_999_999);
      gsm.save();
      if (AuthService.getInstance().isAdmin()) {
        await SyncService.getInstance().updateResources({ gold: gsm.player.resources.gold });
      }
      return mode === 'add'
        ? `Added ${n} gold (total: ${gsm.player.resources.gold})`
        : `Gold set to ${gsm.player.resources.gold}`;
    },
  },
  runes: {
    description: '/runes set <amount> — Set runes | /runes add <amount> — Add runes | /runes set <username> <amount> — Set runes (admin) | /runes add <username> <amount> — Add runes (admin)',
    execute: async (args, gsm) => {
      const mode = args[0] as string | undefined;
      if (mode !== 'set' && mode !== 'add') {
        return 'Usage: /runes set <amount> | /runes add <amount> | /runes set <username> <amount> | /runes add <username> <amount>';
      }
      // Admin grant: /runes set|add <username> <amount>
      if (args.length >= 3) {
        const auth = AuthService.getInstance();
        if (!auth.isAdmin()) return 'Admin access required';
        const username = args[1];
        const amount = parseInt(args[2], 10);
        if (isNaN(amount)) return 'Usage: /runes set <username> <amount>';
        try {
          const res = await fetch(`${getApiBase()}/api/admin/grant-runes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.getToken()}`,
            },
            body: JSON.stringify({ username, amount, mode }),
          });
          const data = await res.json() as { error?: string; total?: number };
          if (!res.ok) return `Error: ${data.error ?? 'request failed'}`;
          return mode === 'set'
            ? `Runes set to ${data.total} for "${username}"`
            : `Added ${amount} runes to "${username}" (total: ${data.total})`;
        } catch (e) {
          return `Request failed: ${e instanceof Error ? e.message : 'unknown error'}`;
        }
      }
      // Self: /runes set|add <amount>
      const n = parseInt(args[1], 10);
      if (isNaN(n)) return 'Usage: /runes set <amount> | /runes add <amount>';
      const current = mode === 'add' ? gsm.player.resources.loot : 0;
      gsm.player.resources.loot = clamp(current + n, 0, 999_999);
      gsm.save();
      if (AuthService.getInstance().isAdmin()) {
        await SyncService.getInstance().updateResources({ loot: gsm.player.resources.loot });
      }
      return mode === 'add'
        ? `Added ${n} runes (total: ${gsm.player.resources.loot})`
        : `Runes set to ${gsm.player.resources.loot}`;
    },
  },
  loot: {
    description: '/loot <amount> — Set loot materials',
    execute: async (args, gsm) => {
      const n = parseInt(args[0], 10);
      if (isNaN(n)) return 'Usage: /loot <amount>';
      gsm.player.resources.loot = clamp(n, 0, 999_999);
      gsm.save();
      if (AuthService.getInstance().isAdmin()) {
        await SyncService.getInstance().updateResources({ loot: gsm.player.resources.loot });
      }
      return `Loot set to ${gsm.player.resources.loot}`;
    },
  },

  xp: {
    description: '/xp <amount> — Add XP to all heroes',
    execute: (args, gsm) => {
      const n = parseInt(args[0], 10);
      if (isNaN(n)) return 'Usage: /xp <amount>';
      if (gsm.player.heroes.length === 0) return 'No heroes to give XP to';
      for (const hero of gsm.player.heroes) {
        addXp(hero, n);
      }
      gsm.save();
      return `Added ${n} XP to all heroes`;
    },
  },
  level: {
    description: '/level <n> — Set zone level (1–100)',
    execute: (args, gsm) => {
      const n = parseInt(args[0], 10);
      if (isNaN(n)) return 'Usage: /level <n>';
      gsm.player.currentLevel = clamp(n, 1, 100);
      gsm.battle.waveNumber = 0;
      gsm.save();
      return `Zone level set to ${gsm.player.currentLevel}`;
    },
  },
  hero: {
    description: '/hero <class> — Add a hero (warrior/mage/archer/healer/rogue/tank)',
    execute: (args, gsm) => {
      const cls = args[0]?.toLowerCase() as HeroClass;
      if (!VALID_CLASSES.includes(cls)) {
        return `Invalid class. Valid: ${VALID_CLASSES.join(', ')}`;
      }
      if (gsm.player.heroes.length >= 10) {
        return 'Hero limit reached (max 10)';
      }
      const name = HERO_CHARACTER_NAMES[cls];
      const id = `cheat_${cls}_${Date.now()}`;
      const hero = createHero(id, name, cls);
      gsm.player.heroes.push(hero);
      gsm.save();
      return `Added ${cls} hero: ${name}`;
    },
  },
  item: {
    description: '/item [rarity] — Generate equipment (common/uncommon/rare/epic/legendary)',
    execute: (args, gsm) => {
      const rarityArg = (args[0] ?? 'rare').toLowerCase();
      if (!VALID_RARITIES.includes(rarityArg as ItemRarity)) {
        return `Invalid rarity. Valid: ${VALID_RARITIES.join(', ')}`;
      }
      const rarity = rarityArg as ItemRarity;
      const lootTier = RARITY_TO_TIER[rarity] ?? 15;
      const level = gsm.player.currentLevel;
      // Retry until we get the desired rarity (up to 50 attempts)
      let item = generateEquipment(level, lootTier, 'boss');
      for (let i = 0; i < 49 && item.rarity !== rarity; i++) {
        item = generateEquipment(level, lootTier, 'boss');
      }
      gsm.player.inventory.push(item);
      gsm.save();
      return `Generated ${item.rarity} equipment: ${item.name}`;
    },
  },
  heal: {
    description: '/heal — Restore all heroes to full HP and MP',
    execute: (_args, gsm) => {
      for (const hero of gsm.player.heroes) {
        hero.stats.hp = hero.stats.maxHp;
        hero.stats.mp = hero.stats.maxMp;
        hero.isDead = false;
        hero.respawnTimer = 0;
      }
      gsm.save();
      return `All heroes healed to full`;
    },
  },
  kill: {
    description: '/kill — Instantly kill all enemies in the current wave',
    execute: (_args, _gsm, game) => {
      if (!game) return 'No game instance';
      const scene = game.scene.getScene('BattleScene') as { killAllEnemies?: () => void } | null;
      if (!scene?.killAllEnemies) return 'Not in battle';
      scene.killAllEnemies();
      return 'All enemies killed';
    },
  },
  god: {
    description: '/god — Toggle invincibility for heroes',
    execute: (_args, gsm) => {
      gsm.godMode = !gsm.godMode;
      const state = gsm.godMode ? 'ON' : 'OFF';
      return `God mode ${state}`;
    },
  },
  speed: {
    description: '/speed <1-5> — Set battle speed multiplier',
    execute: (args, _gsm, game) => {
      const n = parseFloat(args[0] ?? '1');
      if (isNaN(n) || n < 1 || n > 5) return 'Usage: /speed <1-5>';
      if (!game) return 'No game instance';
      const scene = game.scene.getScene('BattleScene') as { setSpeedMultiplier?: (n: number) => void } | null;
      if (!scene?.setSpeedMultiplier) return 'Not in battle';
      scene.setSpeedMultiplier(n);
      return `Battle speed set to ${n}x`;
    },
  },
  wave: {
    description: '/wave <n> — Jump to wave number',
    execute: (args, gsm, game) => {
      const n = parseInt(args[0], 10);
      if (isNaN(n) || n < 1) return 'Usage: /wave <n>';
      gsm.battle.waveNumber = n - 1;
      if (game) {
        const scene = game.scene.getScene('BattleScene') as { startNextWave?: () => void } | null;
        scene?.startNextWave?.();
      }
      return `Wave set to ${n}`;
    },
  },
  unlockall: {
    description: '/unlockall — Max all tomes and base upgrades',
    execute: (_args, gsm) => {
      for (const tome of gsm.player.tomes) {
        const def = TOME_DEFINITIONS[tome.id as keyof typeof TOME_DEFINITIONS];
        if (def) tome.level = def.maxLevel;
      }
      for (const upgrade of gsm.player.base.upgrades) {
        upgrade.currentLevel = upgrade.maxLevel;
      }
      gsm.save();
      return 'All tomes and upgrades maxed';
    },
  },
  admin: {
    description: '/admin — Open admin panel (admin only)',
    execute: () => {
      const auth = AuthService.getInstance();
      if (!auth.isAdmin()) return 'Admin access required';
      new AdminPanel(auth).open();
      return 'Opening admin panel...';
    },
  },
  summongolem: {
    description: '/summongolem — Spawn a Copper Golem on the first deployed hero (test command for the Sorcerer summon pipeline)',
    execute: (_args, gsm, game) => {
      if (!game) return 'No game scene available';
      const battleScene = game.scene.getScene('BattleScene') as unknown as {
        heroSprites?: Array<{ heroRef: { id: string; isDead: boolean; name: string } }>;
        summonCopperGolem?: (owner: unknown) => unknown;
      } | null;
      if (!battleScene) return 'BattleScene not running — start a battle first';
      const owner = battleScene.heroSprites?.find((h) => !h.heroRef.isDead);
      if (!owner) return 'No live hero to act as summon owner';
      const summon = battleScene.summonCopperGolem?.(owner);
      if (!summon) return 'No empty formation slot for the golem';
      void gsm; // not needed beyond the scene reference
      return `Copper Golem summoned by ${owner.heroRef.name}`;
    },
  },
  help: {
    description: '/help — List all cheat commands',
    execute: () => {
      return Object.values(COMMANDS)
        .map((c) => c.description)
        .join('\n');
    },
  },
};

export class CheatCommands {
  constructor(
    private readonly gsm: GameStateManager,
    private readonly game?: PhaserGame,
  ) {}

  async execute(input: string): Promise<string> {
    const trimmed = input.trim().replace(/^\//, '');
    const [cmdName, ...args] = trimmed.split(/\s+/);
    const cmd = COMMANDS[cmdName.toLowerCase()];
    if (!cmd) return `Unknown command: /${cmdName}. Type /help for a list.`;
    return cmd.execute(args, this.gsm, this.game);
  }
}
